<script setup lang="ts">
import type { Game, GameWinnerTileVariant } from '@game/types'
import type { UISelectItem, UISelectItemGroup } from '@core/types'
import { isEmpty } from 'es-toolkit/compat'
import { useGameType, useGameWinnersSearch } from '#imports'

type WinnerTabValue = 'amount' | 'time'

definePageMeta({
    heroPage: 'winners',
    heroSkeleton: 'game',
    disableHeroOnMobile: true,
    termsShown: true,
})

const { site } = useCoreConfig()
const { isMobile } = useDevice()
const { isExisting } = useUserInfo()
const { isHydrating } = useNuxtApp()

// SEO - START
const { data: pageData } = await usePageData('vindere')

const { getHead, getSeoMeta } = useStrapiSeo(
    pageData.value?.seo || {
        metaTitle: `Se alle vinderne på ${site.name} her`,
        metaDescription: `Tusindvis af danskere har vundet på ${site.name}. Se de seneste jackpotvindere lige her. Måske er du den næste heldige vinder?`
    }
)

useHead(getHead())
useSeoMeta(getSeoMeta())
// SEO - END

// Hero
if (import.meta.client && !isHydrating) {
    const layoutContext = useLayoutContext()
    await layoutContext.hero.change({
        page: 'winners',
        disableOnMobile: true
    })
}

// Games
const { isBingo, isSlot } = useGameType()
const page = 1
const page_size = 120

const { data: games } = await useGames({ params: { page, page_size }, filter: { game_types: [GameType.BINGO, GameType.SLOTS], vendors: ['magnet'] }, lazy: true, key: 'game:games-slots-bingo' })

// Combined jackpots and winners
useGamesJackpots()
const { combinedJackpot } = await useGameCombinedJackpot()
const { data: winner } = await useGameLatestJackpotWinner()

const gameCategories = computed(() => {
    if (!games.value?.data?.length) return {}

    const categories = {
        BINGO: games.value.data.filter((x: Game) => isBingo(x.game_type)),
        SLOTS: games.value.data.filter((x: Game) => isSlot(x.game_type)),
    }

    return categories
})

// Winners
const winnerTab = ref<WinnerTabValue>('time')
const selectedGame = ref<string>()

const filters: Array<{
    title: string
    value: WinnerTabValue
}> = [
    {
        title: 'Største',
        value: 'amount'
    },
    {
        title: 'Nyeste',
        value: 'time'
    }
]

const winnerListLimit = 9

const searchQuery = useGameWinnersSearch({
    sortBy: winnerTab,
    gameSlug: selectedGame
})

const { data: winnersLatest } = useGameWinnersLatest({ limit: winnerListLimit })

const selectOptions = computed(() => {
    if (!gameCategories.value) return

    const toItems = (games: Game[]) => sortGamesByName(games).map(game => ({
        label: game.name,
        value: game.slug
    }))

    const groups = ([
        {
            type: 'group',
            label: 'Bingo',
            items: toItems(gameCategories.value?.BINGO ?? [])
        },
        {
            type: 'group',
            label: 'Spilleautomater',
            items: toItems(gameCategories.value?.SLOTS ?? [])
        }
    ] satisfies UISelectItemGroup[]).filter(group => group.items.length > 0)

    return [
        {
            type: 'option',
            label: 'Alle spil',
            value: undefined
        },
        ...groups
    ] as UISelectItem[]
})

const tileVariant = computed<GameWinnerTileVariant>(() => {
    if (!isMobile) return 'long'

    return 'short'
})

function sortGamesByName(games: Game[]) {
    return games.sort((a, b) => {
        const nameA = a.name.toUpperCase() // ignore upper and lowercase
        const nameB = b.name.toUpperCase() // ignore upper and lowercase

        if (nameA < nameB) return -1
        if (nameA > nameB) return 1

        return 0 // Names is equal
    })
}
</script>

<template>
    <div class="vindere">
        <div class="winners-lists">
            <div class="winners-lists__jackpot-mobile">
                <GameCombinedJackpot v-if="isMobile" :jackpot="combinedJackpot || 0" />
                <div class="winners__latest-big">
                    <GameWinnerTile
                        v-if="isMobile && winner?.amount"
                        :winner="winner"
                        is-big-win
                    />
                    <GameWinnerTileSkeleton v-else />
                </div>
            </div>
            <div class="winners-lists__filtered-winners">
                <h1 class="winners-lists__filtered-winners-heading use-brand-font use-brand-font--bold">
                    {{ winnerTab === 'time' ? 'Nyeste vindere' : 'Største vindere' }}
                </h1>
                <div class="winners__container">
                    <section class="winners-filter__heading">
                        <div class="winners-filter__tabs">
                            <ul class="winners-filter__filter">
                                <li
                                    v-for="filter in filters"
                                    :key="filter.value"
                                    class="winners-filter__filter-item"
                                    :class="{ 'winners-filter__filter-item--active': winnerTab === filter.value }"
                                    @click="winnerTab = filter.value"
                                >
                                    {{ filter.title }}
                                </li>
                            </ul>
                        </div>
                        <div class="winner-filter__item--select">
                            <UISelect
                                v-model="selectedGame"
                                name="selectedGame"
                                :items="selectOptions"
                            />
                        </div>
                    </section>

                    <section
                        v-if="searchQuery.isLoading.value"
                        class="text-center"
                    >
                        <UILoader inline>
                            Indlæser
                        </UILoader>
                    </section>

                    <section v-else-if="searchQuery.data.value?.winners?.length">
                        <LazyGameWinnerList
                            :winners="searchQuery.data.value?.winners"
                            type="winners-page"
                            :tile-variant="tileVariant"
                            :fade="false"
                        />
                    </section>

                    <p
                        v-else
                        class="text-center"
                    >
                        {{ searchQuery.data.value?.message }}
                    </p>
                </div>
            </div>
            <div class="winners-lists__sidebar">
                <div class="winners-lists__jackpot-desktop">
                    <GameCombinedJackpot v-if="!isMobile" :jackpot="combinedJackpot || 0" />
                    <div class="winners__latest-big">
                        <GameWinnerTile
                            v-if="!isMobile && winner?.amount"
                            :winner="winner"
                            is-big-win
                        />
                        <GameWinnerTileSkeleton v-else />
                    </div>
                </div>
                <h2 class="use-brand-font use-brand-font--bold winners-now__head">
                    Vindere lige nu
                </h2>
                <LazyGameWinnerList
                    v-if="winnersLatest?.length"
                    class="winners__latest"
                    :winners="winnersLatest"
                    :fade="true"
                />
                <div
                    v-else
                    class="winners__latest"
                >
                    <GameWinnerTileSkeleton
                        v-for="index in winnerListLimit"
                        :key="index"
                    />
                </div>
            </div>
        </div>

        <LazyNimbusStrapiDynamicRenderer
            v-if="!isExisting && !isEmpty(pageData?.dynamicContent)"
            :content="pageData?.dynamicContent"
            :max-width="1024"
        />
        <LazyTrustpilotReview v-if="!isExisting" />
    </div>
</template>

<style lang="scss" scoped>
.winners__container {
    min-height: 733px;
}

.winners-filter {
    &__heading {
        margin-bottom: 5px;

        @media (min-width: $screen-md) {
            display: flex;
            justify-content: space-between;
        }
    }

    &__filter {
        display: flex;
        justify-content: center;
        margin-bottom: 7px;
        padding: 0;
        font-size: $font-size-sm;
        list-style-type: none;

        @media (min-width: $screen-md) {
            font-size: $font-size-base;
        }

        &-item {
            padding: 8px 10px 0;
            border-bottom: 2px solid var(--disabled, $gray);
            color: var(--on-disabled, $gray);
            font-weight: $text-medium;
            cursor: pointer;
            transition: border-bottom 0.4s;

            &:hover {
                color: var(--secondary, $blue);
            }

            &--active {
                border-bottom: 2px solid var(--secondary, $blue);
                color: var(--secondary, $blue);
            }

            &--select {
                width: 100%;

                @media (min-width: $screen-md) {
                    width: auto;
                }

                select {
                    min-width: 150px;
                    border-radius: var(--border-radius, $border-radius);
                }
            }
        }
    }
}

.winners-lists {
    display: flex;

    @media (max-width: #{$screen-md - 1 }) {
        flex-direction: column;
    }

    &__filtered-winners {
        flex: 1;

        @media (min-width: $screen-md) {
            padding-right: 15px;
        }

        &-heading {
            text-align: center;

            @media (min-width: $screen-md) {
                text-align: left;
            }
        }
    }

    .winners__latest {
        padding-top: 16px;
        padding-top: 0;

        @media (min-width: $screen-md) {
            min-width: 244px;
        }

        :deep(.winners__shadow) {
            background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, $white 100%);
        }
    }

    .winners__latest-big {
        position: relative;
        border-radius: 6px;
        min-height: 58px;
        padding: 0 4px;
        margin-bottom: 10px;
    }

    :deep(.combined-jackpot__title) {
        padding: 0;
    }

    &__jackpot-desktop {
        display: none;

        @media (min-width: $screen-md) {
            display: block;
        }

        @media (min-width: $screen-md) and (max-width: #{$screen-lg - 1}) {
            margin-top: 16px;
        }
    }

    &__jackpot-mobile {
        display: none;

        @media (max-width: #{$screen-md - 1}) {
            display: block;
            margin-top: 16px;
        }
    }

    &__sidebar {
        @media (min-width: $screen-md) {
            width: 30%;
        }

        @media (min-width: $screen-xl) {
            width: 300px;
        }
    }
}

.winners-now__head {
    text-align: center;

    @media (min-width: $screen-md) {
        font-size: $font-size-base;
        margin: 17px 0 10px;
    }

    @media (min-width: $screen-lg) {
        font-size: $font-size-xl;
        margin: 33px 0 10px;
    }
}
</style>
